import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

// Components
import { Text, Page, ExternalLinkButton } from '../../..'
import { OverlayQuestionsFooter } from '../../Routing'
import { VerificationFlowProvider, VerificationFlowManager } from '../../VerificationFlow'

// Constants
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'
import { ROUTE_KEY_BUY } from '../../../../constants/routes'

// Selectors
import { selectRouteKey } from '../../../../store/selectors/routes'
import { 
  selectPreferredRealEstateAgentMembership,
  selectPreferredLoanOfficerMembership 
} from '../../../../store/selectors/clientTeamMemberships'

// Hooks
import { useVerificationFlowEnabled } from '../../../../hooks'

// Utils
import airDnaLogo from './AirDNA-No-Padding.svg'
import airDnaLearnMoreImage from './AirDNA-LearnMore.png'

// Styles
import styles from './AirDnaLearnMorePanel.module.scss'

const propTypes = {
  withCurrentHomeData: PropTypes.bool
}

const messages = defineMessages({
  imageAlt: {
    id: 'Questions.airdna.imageAlt',
    defaultMessage: 'Your rental potential'
  }
})

const AirDnaLearnMorePanel = () => {
  const isBuyer = selectRouteKey() === ROUTE_KEY_BUY
  const intl = useIntl()
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const realEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)

  const topic = isBuyer ? CUSTOMER_MESSAGES.BUYER_SHORT_TERM_RENTALS : CUSTOMER_MESSAGES.HOMEOWNER_SHORT_TERM_RENTALS
  const verifiedTopicKey = isBuyer ? CUSTOMER_MESSAGES.HBN_BUYER_SHORT_TERM_RENTALS.topicKey : CUSTOMER_MESSAGES.HBN_HOMEOWNER_SHORT_TERM_RENTALS.topicKey
  return (
    <Page>
      <Page.Body>
        <img className={styles.image} src={airDnaLearnMoreImage} alt={intl.formatMessage(messages.imageAlt)} />
        <Text size='xl' className={styles.headline} variant='neutralCool' variantCount='900'>
          <FormattedMessage
            id='AirDna.additionalInfo.headline'
            defaultMessage='Rentals are a great way to make extra income'
          />
        </Text>
        <Text size='s' variant='neutralCool' variantCount='700' align='left'>
          <FormattedMessage
            id='AirDna.additionalInfo.subheadline'
            defaultMessage='Sites like {airbnb} and {vrbo} make it really easy to rent out a room or your entire house on a nightly basis to people staying in town. It‘s great income and can be a fun way to help people get to know your city.'
            values={{ airbnb: AirbnbLink, vrbo: VrboLink }}
          />
        </Text>
        <Text size='xl' className={styles.subHeadline} variant='neutralCool' variantCount='900'>
          <FormattedMessage
            id='AirDna.additionalInfo.headlineTwo'
            defaultMessage='Make sure you‘re allowed to rent your home'
          />
        </Text>
        <Text size='s' paragraph variant='neutralCool' variantCount='700' align='left'>
          <FormattedMessage
            id='AirDna.additionalInfo.descriptionParaOne'
            defaultMessage='Some cities have laws that restrict your ability to host paying guests for short periods. These laws are often part of a city‘s zoning or administrative codes. In many cities, you must register, get a permit, or obtain a license before you list your property or accept guests. Also check to make sure your loan terms allow for short term rental.'
          />
        </Text>
        <Text size='s' variant='neutralCool' variantCount='700' align='left'>
          <FormattedMessage
            id='AirDna.additionalInfo.descriptionParaTwo'
            defaultMessage='These rules can be confusing. It‘s smart to work with your real estate agent to clarify these rules so that you have a clear understanding of what the laws are.'
          />
        </Text>
        <a href='https://www.airdna.co/' target='_blank' rel='noopener noreferrer' className={styles.attribution}>
          <div>
            <FormattedMessage id='AirDNA.attribution' defaultMessage='Powered by' />
          </div>
          <div>
            <img className={styles.imgAirdna} src={airDnaLogo} alt='airdna' />
          </div>
        </a>
      </Page.Body>

      <Page.Footer>
        <VerificationFlowProvider
          options={{
            enableVerificationFlow: isVerificationFlowEnabled,
            initialPartnerType: 'RealEstateAgent',
            flowType: 'real estate',
            triggerOnMessageSent: true,
            realEstateAgentMembership: realEstateAgentMembership,
            loanOfficerMembership: loanOfficerMembership,
            includePrequal: false
          }}
        >
          <VerificationFlowManager />
          <OverlayQuestionsFooter
            topic={topic.topic}
            topicKey={topic.topicKey}
            questions={[topic.message, topic.secondaryMessage]}
            disableCospo
            placeholder
            requiresPartnerVerification={isVerificationFlowEnabled}
            verifiedQuestionTopicKey={verifiedTopicKey}
            partnerType='RealEstateAgent'
            flowType='real estate'
          />
        </VerificationFlowProvider>
      </Page.Footer>
    </Page>
  )
}

const AirbnbLink = (
  <ExternalLinkButton minimal href='https://www.airbnb.com'>
    Airbnb
  </ExternalLinkButton>
)

const VrboLink = (
  <ExternalLinkButton minimal href='http://www.vrbo.com'>
    VRBO
  </ExternalLinkButton>
)

AirDnaLearnMorePanel.propTypes = propTypes

export default AirDnaLearnMorePanel
